// src/pages/Home.jsx
import React, { useEffect, useRef, useCallback } from "react";
import { motion, useAnimation, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import FaqSection from "../components/FaqSection";
import CtaBanner from "../components/CtaBanner";
import ContactSection from "../components/ContactSection";

// Lazy chunks
const ServicesSection = React.lazy(() => import("../components/ServicesSection"));
const FloatingChat = React.lazy(() => import("../components/FloatingChat"));

/* =========================
   ANIMATION PRESETS
========================= */
const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};
const fadeStagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
};
const scaleIn = {
  hidden: { opacity: 0, scale: 0.96 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" } },
};

// Section wrapper
const Section = ({ id, children }) => (
  <motion.section
    id={id}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.25 }}
    variants={fadeUp}
  >
    {children}
  </motion.section>
);

// Title
const AnimatedTitle = ({ id, children }) => (
  <div className="text-center">
    <motion.h2 id={id} className="text-3xl font-bold text-sky-800 md:text-4xl" variants={fadeUp}>
      {children}
    </motion.h2>
    <motion.div
      aria-hidden="true"
      className="mx-auto mt-4 h-1 w-24 origin-left rounded bg-gradient-to-r from-sky-500 via-indigo-400 to-sky-500"
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true, amount: 0.6 }}
      transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
    />
  </div>
);

const Divider = () => <div className="my-6" aria-hidden="true" />;

// Info card
const InfoCard = ({ icon, title, text }) => (
  <motion.div
    variants={scaleIn}
    className="rounded-2xl border border-sky-100 bg-white/70 p-6 shadow-sm backdrop-blur hover:shadow-md"
  >
    <div className="text-2xl">{icon}</div>
    <h3 className="mt-2 text-lg font-semibold text-gray-900">{title}</h3>
    <p className="mt-1 text-sm text-gray-600">{text}</p>
  </motion.div>
);

/* =========================
   BACKGROUND ORNAMENTS
========================= */
const ParallaxBlobs = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const y3 = useTransform(scrollYProgress, [0, 1], [-20, 40]);

  return (
    <div ref={ref} className="pointer-events-none absolute inset-0 overflow-hidden">
      <motion.div style={{ y: y1 }} className="absolute -left-16 top-24 h-72 w-72 rounded-full bg-sky-100/60 blur-2xl" />
      <motion.div style={{ y: y2 }} className="absolute right-[-60px] top-32 h-80 w-80 rounded-full bg-indigo-100/60 blur-2xl" />
      <motion.div style={{ y: y3 }} className="absolute bottom-10 left-1/3 h-64 w-64 rounded-full bg-sky-50/80 blur-2xl" />
    </div>
  );
};

const PastelBackground = ({ children }) => (
  <div className="relative min-h-screen bg-white [background:radial-gradient(1200px_600px_at_100%_-10%,#eaf6ff_0%,transparent_50%)]">
    <ParallaxBlobs />
    {children}
  </div>
);

/** Stats strip (your exact figures) */
const StatsStrip = () => (
  <section className="relative mx-auto -mt-10 max-w-7xl px-4" aria-label="Glide at a glance">
    <motion.div
      variants={fadeStagger}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.4 }}
      className="grid gap-4 rounded-2xl border border-sky-100 bg-white/80 p-5 shadow-sm backdrop-blur sm:grid-cols-2 lg:grid-cols-4"
    >
      {[
        { k: "700+", v: "Students Guided" },
        { k: "7+ yrs", v: "Experience" },
        { k: "1:1", v: "Student Support" },
        { k: "50+", v: "Partner Institutions" },
      ].map((item, i) => (
        <motion.div
          key={i}
          variants={scaleIn}
          className="flex items-center justify-center gap-3 rounded-xl bg-gradient-to-br from-sky-50 to-indigo-50 p-4"
        >
          <span className="text-3xl font-extrabold text-sky-600">{item.k}</span>
          <span className="text-sm font-medium text-gray-700">{item.v}</span>
        </motion.div>
      ))}
    </motion.div>
  </section>
);

/* =========================
   CONTENT
========================= */
const WhyAustralia = () => (
  <section className="relative overflow-hidden bg-white py-16" aria-labelledby="why-australia-title">
    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(80rem_40rem_at_50%_-10%,#e0f2fe_0%,transparent_60%)]" />
    <div className="relative mx-auto max-w-7xl px-4 text-center">
      <AnimatedTitle id="why-australia-title">
        Why <span className="text-sky-600">Australia?</span>
      </AnimatedTitle>
      <Divider />
      <motion.p variants={fadeUp} className="mx-auto max-w-3xl text-gray-600">
        World‑class universities, vibrant cities, generous work rights, and strong post‑study pathways
        make Australia a top choice for global students.
      </motion.p>

      <motion.div
        variants={fadeStagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3 }}
        className="mx-auto mt-10 grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-3"
      >
        <InfoCard icon="🎓" title="Top-ranked" text="Globally recognised degrees with practical, industry-led curricula." />
        <InfoCard icon="💼" title="Work & Study" text="Gain experience and support living costs with flexible work rights." />
        <InfoCard icon="🛡️" title="Safe & Diverse" text="Welcoming communities and excellent student support." />
        <InfoCard icon="🧭" title="PR Pathways" text="Skilled migration options and state-specific opportunities." />
        <InfoCard icon="🎯" title="Scholarships" text="Merit & need-based awards across institutions and states." />
        <InfoCard icon="🚀" title="Post‑Study Work" text="Build career experience on the 485 visa after graduation." />
      </motion.div>
    </div>
  </section>
);

const WhyChooseUs = () => (
  <section className="relative overflow-hidden bg-white py-16" aria-labelledby="why-choose-us-title">
    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(80rem_40rem_at_50%_-10%,#e0ecff_0%,transparent_60%)]" />
    <div className="relative mx-auto max-w-7xl px-4 text-center">
      <AnimatedTitle id="why-choose-us-title">
        Why <span className="text-sky-600">Choose Us</span>
      </AnimatedTitle>
      <Divider />
      <motion.p variants={fadeUp} className="mx-auto max-w-3xl text-gray-600">
        Expert, personalised guidance from course shortlist to visa lodgement—clear steps, strong files, smoother outcomes.
      </motion.p>

      <motion.div
        variants={fadeStagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3 }}
        className="mx-auto mt-10 grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-4"
      >
        <InfoCard icon="🎓" title="Expert Counsellors" text="Australia‑focused advisors with real admission insight." />
        <InfoCard icon="🤝" title="End‑to‑End Support" text="Shortlist, SOP, GTE, COE, visa—handled with care." />
        <InfoCard icon="🧾" title="Transparent Process" text="No surprises: checklists, timelines, clear communication." />
        <InfoCard icon="⚡" title="Faster Turnaround" text="Well‑prepared files move quicker through reviews." />
      </motion.div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="relative bg-gradient-to-b from-white to-sky-50/50 py-16">
    <div className="mx-auto max-w-7xl px-4">
      <AnimatedTitle>
        Your Journey, <span className="text-sky-600">Simple & Clear</span>
      </AnimatedTitle>
      <Divider />
      <motion.ol
        variants={fadeStagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3 }}
        className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4"
      >
        {[
          { n: 1, t: "Consult", d: "Understand goals, budget, and timelines." },
          { n: 2, t: "Shortlist & Docs", d: "Pick best‑fit courses; organise documents." },
          { n: 3, t: "Apply & COE", d: "Submit strong applications, secure offers/COE." },
          { n: 4, t: "Visa & Fly", d: "Prepare GTE/SOP, lodge visa, pre‑departure help." },
        ].map((s, i) => (
          <motion.li key={i} variants={scaleIn} className="relative rounded-2xl border border-sky-100 bg-white/80 p-6 shadow-sm backdrop-blur">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-sky-600 text-sm font-bold text-white">
              {s.n}
            </span>
            <h3 className="mt-3 text-lg font-semibold text-gray-900">{s.t}</h3>
            <p className="mt-1 text-sm text-gray-600">{s.d}</p>
          </motion.li>
        ))}
      </motion.ol>
    </div>
  </section>
);

/* =========================
   VISA TYPES (marquee with guaranteed clickable cards)
========================= */
const VISA_TYPES = [
  { title: "Student Visa (Subclass 500)", desc: "For full‑time study at Australian universities, colleges, and schools.", to: "/visa/student-visa-500" },
  { title: "Post‑Study Work Visa (Subclass 485)", desc: "Gain Australian work experience after completing eligible study.", to: "/visa/post-study-work-visa-485" },
  { title: "Tourist / Visitor Visa (Subclass 600)", desc: "Visit for holidays, family trips, or short business activities.", to: "/visa/tourist-visa-600" },
];

const VisaCard = ({ item }) => (
  <Link
    to={item.to}
    className="group block w-[290px] sm:w-[320px] min-h-[180px] flex-shrink-0 rounded-2xl border border-sky-100 bg-white/80 p-6 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-sky-400"
    onMouseDown={(e) => e.currentTarget.focus()}
    style={{ willChange: "transform" }}
  >
    <h3 className="text-lg font-semibold text-sky-800 whitespace-normal break-words">{item.title}</h3>
    <p className="mt-2 text-sm text-gray-600 whitespace-normal break-words">{item.desc}</p>
    <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-sky-700">
      Discover More <span aria-hidden>→</span>
    </span>
  </Link>
);

const VisaTypesMarquee = () => {
  const controls = useAnimation();
  const isPausedRef = useRef(false);
  const items = [...VISA_TYPES, ...VISA_TYPES]; // seamless loop

  const startScroll = useCallback(() => {
    if (isPausedRef.current) return;
    controls.start({
      x: ["0%", "-50%"],
      transition: { duration: 28, repeat: Infinity, ease: "linear" },
    });
  }, [controls]);

  const pauseScroll = useCallback(() => {
    isPausedRef.current = true;
    controls.stop();
  }, [controls]);

  const resumeScroll = useCallback(() => {
    isPausedRef.current = false;
    startScroll();
  }, [startScroll]);

  useEffect(() => {
    startScroll();
    const onVis = () => {
      if (!document.hidden && !isPausedRef.current) startScroll();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [startScroll]);

  return (
    <section className="py-16" aria-labelledby="visa-types-title">
      <div className="mx-auto max-w-7xl px-4">
        <AnimatedTitle id="visa-types-title">
          Explore <span className="text-sky-600">Visa Types</span>
        </AnimatedTitle>
        <Divider />
        <div
          className="relative overflow-hidden"
          onMouseEnter={pauseScroll}
          onMouseLeave={resumeScroll}
          onTouchStart={pauseScroll}
          onTouchEnd={resumeScroll}
          onTouchCancel={resumeScroll}
        >
          <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-white to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white to-transparent" />
          <motion.div animate={controls} className="flex gap-6" style={{ willChange: "transform" }}>
            {items.map((item, i) => (
              <VisaCard key={`${item.to}-${i}`} item={item} />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* =========================
   SIMPLE MOVING TESTIMONIALS
========================= */
const TESTIMONIALS = [
  { n: "Aarav", t: "Got my offer and visa smoothly. Team explained each step clearly." },
  { n: "Sofia", t: "Great shortlisting and SOP help. Landed a scholarship too!" },
  { n: "Vivek", t: "Their visa checklist was on point. COE + 485 planning done early." },
  { n: "Min", t: "Quick responses even after hours. 24/7 support is real." },
  { n: "Natasha", t: "Loved the transparency—no surprises, just results." },
];

const TestimonialCard = ({ item }) => (
  <div className="w-[320px] sm:w-[380px] min-h-[140px] flex-shrink-0 rounded-2xl border border-sky-100 bg-white/80 p-6 shadow-sm backdrop-blur">
    <p className="text-sm text-gray-700 leading-relaxed">“{item.t}”</p>
    <div className="mt-3 text-sm font-semibold text-sky-800">— {item.n}</div>
  </div>
);

const TestimonialsTicker = () => {
  const controls = useAnimation();
  const isPausedRef = useRef(false);
  const items = [...TESTIMONIALS, ...TESTIMONIALS, ...TESTIMONIALS];

  const startScroll = useCallback(() => {
    if (isPausedRef.current) return;
    controls.start({
      x: ["0%", "-66.66%"],
      transition: { duration: 36, repeat: Infinity, ease: "linear" },
    });
  }, [controls]);

  const pauseScroll = useCallback(() => {
    isPausedRef.current = true;
    controls.stop();
  }, [controls]);

  const resumeScroll = useCallback(() => {
    isPausedRef.current = false;
    startScroll();
  }, [startScroll]);

  useEffect(() => {
    startScroll();
    return () => controls.stop();
  }, [startScroll, controls]);

  return (
    <section className="relative py-16" aria-labelledby="testimonials-title">
      <div className="mx-auto max-w-7xl px-4">
        <AnimatedTitle id="testimonials-title">
          What <span className="text-sky-600">Students Say</span>
        </AnimatedTitle>
        <Divider />
        <div
          className="relative overflow-hidden"
          onMouseEnter={pauseScroll}
          onMouseLeave={resumeScroll}
          onTouchStart={pauseScroll}
          onTouchEnd={resumeScroll}
          onTouchCancel={resumeScroll}
        >
          <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-white to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white to-transparent" />
          <motion.div animate={controls} className="flex gap-6" style={{ willChange: "transform" }}>
            {items.map((item, i) => (
              <TestimonialCard key={`${item.n}-${i}`} item={item} />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* =========================
   PAGE
========================= */
const Home = () => {
  return (
    <PastelBackground>
      <Hero scrollTarget="#services" srcMp4="/assets/hero.mp4" poster="/assets/hero-poster.jpg" />

      <StatsStrip />

      <Section id="why-australia">
        <WhyAustralia />
      </Section>

      <Section id="services">
        <React.Suspense
          fallback={
            <div className="mx-auto max-w-7xl px-4 py-16" aria-busy="true" aria-live="polite">
              <div className="h-8 w-48 animate-pulse rounded bg-sky-100" />
              <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-40 animate-pulse rounded-2xl bg-sky-50" />
                ))}
              </div>
            </div>
          }
        >
          <motion.div variants={fadeStagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }}>
            <ServicesSection />
          </motion.div>
        </React.Suspense>
      </Section>

      <Section id="why-choose-us">
        <WhyChooseUs />
      </Section>

      <Section id="how-it-works">
        <HowItWorks />
      </Section>

      <Section id="visa-types">
        <VisaTypesMarquee />
      </Section>

      <Section id="testimonials">
        <motion.div variants={fadeStagger}>
          <TestimonialsTicker />
        </motion.div>
      </Section>

      <Section id="faq">
        <motion.div variants={fadeStagger}>
          <FaqSection />
        </motion.div>
      </Section>

      <Section id="call-to-action">
        <motion.div variants={scaleIn}>
          <CtaBanner
            title="Ready to Glide Forward?"
            subtitle="A quick chat can save weeks. Get friendly, expert guidance today."
            buttonText="Book Free Consultation"
            buttonLink="/contact"
          />
        </motion.div>
      </Section>

      <Section id="contact">
        <motion.div variants={fadeUp}>
          <ContactSection />
        </motion.div>
      </Section>

      <React.Suspense fallback={null}>
        <FloatingChat />
      </React.Suspense>
    </PastelBackground>
  );
};

export default Home;
